import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { PayPalNamespace } from '@paypal/paypal-js';
import {
	Appearance,
	PaymentMethod,
	Stripe,
	StripeAddressElement,
	StripeAddressElementOptions,
	StripeCardElement,
	StripeElements,
} from '@stripe/stripe-js';
import { Stripe as StripeTypes } from 'stripe';
import to from 'await-to-js';
import * as _ from 'lodash';
import { distinctUntilChanged } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { CheckoutService } from '../../../core/services/checkout.service';
import { GenreService } from '../../../core/services/data/genre.service';
import { OrderService } from '../../../core/services/data/order.service';
import { SavedPaymentsMethodsService } from '../../../core/services/data/saved-payment-methods.service';
import { SettingsService } from '../../../core/services/data/settings.service';
import { UserService } from '../../../core/services/data/user.service';
import { InputControlService } from '../../../core/services/input-control.service';
import { LogsService } from '../../../core/services/logs.service';
import { ModalService } from '../../../core/services/modal.service';
import { PaypalService } from '../../../core/services/paypal.service';
import { StripeService } from '../../../core/services/stripe.service';
import { IFeature, IOrder, IPromocode, IUser } from '../../../types/datatypes';
import { ProcessPaymentEvent, SavedPaymentMethod } from '../../../types/local';
import { IPopulatedGenres, IPopulatedStripePaymentMethod } from '../../../types/populated';
import {
	PaypalCreateOrderResponse,
	StripeCustomerReponse,
	StripePaymentMethodsResponse,
} from '../../../types/responses';
import { StripeFormFields } from './stripe-form-fields';

@Component({
	selector: 'app-stripe-form',
	templateUrl: './stripe-form.component.html',
	styleUrls: ['./stripe-form.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class StripeFormComponent implements OnInit, OnDestroy, AfterViewInit {
	@Input() info;

	@Input() inProgress;

	@Input() feature: IFeature;

	@Input() promoCode: IPromocode;

	/**
	 * Only used by paypal because it can fail or get cancelled after
	 * calling actions.restart() in the parent component.
	 */
	@Output() paymentCancelled = new EventEmitter();

	@Output() processPayment = new EventEmitter<ProcessPaymentEvent>();

	public order: IOrder;

	public orderSub: Subscription;

	public errorText;

	public optedInToSaveCard: boolean;

	public errorTitle;

	public errorExtra;

	public paypalDisabled = false;

	public form;

	public formComponents = new StripeFormFields();

	public user: IUser;

	public stripe: Stripe;

	public elements: StripeElements;

	public stripeCustomer: StripeTypes.Customer;

	public stripeCardElement: StripeCardElement;

	public stripeAddress: StripeAddressElement;

	public stripePaymentError = '';

	public stripePaymentMethods: PaymentMethod[] = [];

	public stripePrimaryPaymentMethod = null;

	public stripeSelectedPaymentMethodId = '';

	public genres: IPopulatedGenres[];

	public savedPaymentMethods: SavedPaymentMethod[] = [];

	public showCardForm = false;

	public addressOptions: StripeAddressElementOptions = {
		mode: 'billing',
		defaultValues: {
			name: '',
			address: {
				city: '',
				country: '',
				line1: '',
				line2: '',
				postal_code: '',
				state: '',
			},
		},
	};

	public submitted = false;

	public checkoutSignupLogin = false;

	public errorOptions = {
		title: 'There was a problem',
		text: null,
		actions: [{
			type: 'solid',
			text: 'Got it',
			callback: () => this.modalService.close('payment-error-modal'),
		}],
	};

	private paymentMethodsLoaded = false;

	private payPal: PayPalNamespace;

	private subscriptions$ = new Subscription();

	constructor(
		private ics: InputControlService,
		private modalService: ModalService,
		private os: OrderService,
		private us: UserService,
		private ss: StripeService,
		private ls: LogsService,
		private settingsService: SettingsService,
		private genreService: GenreService,
		private checkoutService: CheckoutService,
		private savedPaymentMethodsService: SavedPaymentsMethodsService,
		private paypalService: PaypalService,
	) { }

	ngAfterViewInit(): void {
		this.initStripeElements();
		this.initPayPal();
	}

	ngOnInit() {
		if (this.info.email) {
			this.formComponents.email.value = this.info.email;
		}

		this.settingsService.getCheckoutSignupLoginSetting().then(
			(value) => {
				this.checkoutSignupLogin = !!value;
			},
		);

		this.form = InputControlService.toFormGroup(this.formComponents);
		this.subscriptions$.add(this.us.user$.subscribe((data: any) => {
			if (data) {
				this.user = data;
				if (this.user?.username) {
					this.form.get('email').setValue(this.user.username);
					this.handleStripeCustomer();
					this.savedPaymentMethodsService.list(this.user._id).subscribe((savedMethodsResponse) => {
						if (savedMethodsResponse.success) {
							this.savedPaymentMethods = savedMethodsResponse.savedPaymentMethods;
							this.getStripePaymentMethods();
						}
					});
					this.addressOptions.defaultValues = this.populateAddressDefaultValues();
				} else {
					this.form.get('email').setValue(this.info.email);
				}
			}
		}));

		this.subscriptions$.add(this.genreService.getGenres().subscribe((genres: IPopulatedGenres[]) => {
			this.genres = genres;
		}));

		// On order change, re-check paypal
		this.orderSub = this.os.orderStore$
			.pipe(distinctUntilChanged((prev, curr) => _.isEqual(prev, curr)))
			.subscribe((order: IOrder) => {
				this.order = order;
				this.paypalDisabled = this.isSubscription(order);
			});
	}

	ngOnDestroy(): void {
		this.orderSub?.unsubscribe();
		this.subscriptions$.unsubscribe();
	}

	async initPayPal() {
		this.payPal = await this.paypalService.getInstance();
		const buttons = this.payPal.Buttons({
			style: {
				shape: 'pill',
			},
			createOrder: async () => {
				this.ls.postLog({
					type: 'PayPal',
					message: `Transaction initiated for feature: ${this.order._id}`,
				});
				const [createError, createResponse] = await to(this.createOrder());

				if (createError) {
					this.ls.postLog({
						type: 'PayPal',
						message: `Failed to create PayPal order for feature: ${this.order._id}. ${createError.message}`,
					});
					throw new Error('Failed to create paypal order.');
				}

				if (!createResponse.success) {
					throw new Error(createResponse.message);
				}

				return createResponse.order.id;
			},
			onApprove: async (data, actions) => {
				this.processPayment.emit({ type: 'paypal', orderID: data.orderID, actions });
			},
			onCancel: () => {
				/**
				 * This isn't an error but is treated as one...
				 */
				const response = this.checkoutService.handleCancelled(this.order, 'PayPal');
				this.handleError(response);
				this.paymentCancelled.emit();
			},
			onError: (error) => {
				const parsedError = this.extractPayPalError(error);
				const _error = !parsedError || parsedError === '' ? 'Failed to pay with paypal' : parsedError;
				this.checkoutService.log('PayPal', this.order, 'error', _error);
				this.handleError({ errorText: [error?.message ?? 'Something went wrong. Unknown error :('] });
				this.paymentCancelled.emit();
			},
		});
		buttons.render('#paypal-button');
	}

	async createOrder() {
		return new Promise<PaypalCreateOrderResponse>((resolve, reject) => {
			this.paypalService.createOrder().subscribe({
				next: (response) => {
					resolve(response);
				},
				error: (error) => {
					reject(error);
				},
			});
		});
	}

	isSubscription(order) {
		if (!order?.items) {
			return false;
		}
		const subscription = order.items.find((item) => item.itemType === 'subscription');

		return !!subscription;
	}

	/**
	 * Handles payments with stripe.
	 */
	async onSubmit(event) {
		this.submitted = true;
		event.preventDefault();
		/**
		 * Validate form (is it only the email field?)
		 */
		const errors = this.ics.checkFormValid(this.form, this.formComponents);
		if (errors.hasErrors) {
			this.handleError({ errorText: errors.errorText });
			return;
		}
		let paymentMethod: string;
		if (this.stripeSelectedPaymentMethodId) {
			paymentMethod = this.stripeSelectedPaymentMethodId;
		} else {
			const [err, method] = await to(this._createPaymentMethod());
			if (err) {
				this.handleError({ errorText: err.message });
				return;
			}
			paymentMethod = method;
		}
		this.processPayment.emit({
			type: 'stripe',
			paymentMethod,
			savePaymentMethod: this.optedInToSaveCard && (!this.stripeSelectedPaymentMethodId),
		});
		/**
		 * Update shipping address once the customer has committed to pay.
		 */
		await this.updateUserShippingAddress();
	}

	async _createPaymentMethod() {
		const [error, address] = await to(this.stripeAddress.getValue());
		if (error) {
			this.handleError({ errorText: error.message });
			throw error;
		}
		if (!address.complete) {
			this.handleError({ errorText: 'Could not collect address details.' });
			throw new Error('Could not collect address details.');
		}
		const billing = address.value;
		const [methodErr, method] = await to(this.stripe.createPaymentMethod({
			type: 'card',
			card: this.stripeCardElement,
			billing_details: {
				address: billing?.address,
				name: billing?.name,
			},
		}));
		if (methodErr || method.error) {
			this.handleError({ errorText: methodErr?.message ?? method?.error?.message ?? 'Unknown error' });
			throw new Error(methodErr?.message ?? method?.error?.message ?? 'Unknown error');
		}
		const paymentMethod = method.paymentMethod.id;
		return paymentMethod;
	}

	handleError({ errorText, errorExtra = null, errorTitle = '' }) {
		this.errorText = errorText;
		this.errorTitle = errorTitle;
		this.errorExtra = errorExtra;
		this.submitted = false;

		this.errorOptions.text = [errorText];

		this.modalService.open('payment-error-modal');
	}

	async initStripeElements() {
		await this._initStripeSDK();
		this._generateStripeForm();
	}

	async _initStripeSDK() {
		if (this.stripe) return;
		this.stripe = await this.ss.getInstance();
	}

	useNewCard() {
		this.showCardForm = true;
		this.stripeSelectedPaymentMethodId = null;
	}

	populateAddressDefaultValues() {
		const { user } = this;
		const { shippingInfo } = user;
		const defaultValues = {
			name: `${user.firstName} ${user.lastName}`,
			address: {
				city: this.isset(shippingInfo?.city),
				country: this.isset(shippingInfo?.country),
				line1: this.isset(shippingInfo?.address?.street1),
				line2: this.isset(shippingInfo?.address?.street2),
				postal_code: this.isset(shippingInfo?.postalCode?.toString()),
				state: this.isset(shippingInfo?.state),
			},
		};
		return defaultValues;
	}

	isset(value) { return value || ''; }

	handleStripeCustomer() {
		if (this.user.stripeCustomerId) {
			return this.getStripeCustomer();
		}

		return this.checkStripeCustomer();
	}

	checkStripeCustomer(): Promise<string> {
		return new Promise((resolve) => {
			this.ss.createStripeCustomer(this.user.username).subscribe((result: StripeCustomerReponse) => {
				if (!result.success) return resolve(null);

				this.user.stripeCustomerId = result.customer.id;
				this.stripeCustomer = result.customer;

				const fieldsToUpdate = {
					stripeCustomerId: result.customer.id,
				};

				this.us.saveProfile(this.user._id, fieldsToUpdate).subscribe();
				this.us.updateUser(this.user);
				this.us.updateAuthor(this.user);

				return resolve(result.customer.id);
			});
		});
	}

	getStripeCustomer(): Promise<string> {
		return new Promise((resolve) => {
			this.ss.getCustomer(this.user.stripeCustomerId).subscribe((result: StripeCustomerReponse) => {
				if (!result.success) return resolve(null);

				this.stripeCustomer = result.customer;
				return resolve(result.customer.id);
			});
		});
	}

	async updateUserShippingAddress() {
		if (!this.user) return;

		const [error, resultValues] = await to(this.stripeAddress.getValue());
		if (error) {
			throw error;
		}

		const billing = resultValues?.value || null;
		const { address } = billing;
		const { line1, line2, city, country, state, postal_code: postalCode } = address;

		let fieldsToUpdate = {
			shippingInfo: {
				address: {
					street1: line1,
					street2: line2,
				},
				city,
				country,
				state,
				postalCode,
			},
		};

		if (!fieldsToUpdate.shippingInfo.address.street1) {
			fieldsToUpdate = _.omit(fieldsToUpdate, 'shippingInfo.address.street1');
		}
		if (!fieldsToUpdate.shippingInfo.address.street2) {
			fieldsToUpdate = _.omit(fieldsToUpdate, 'shippingInfo.address.street2');
		}
		if (Object.keys(fieldsToUpdate.shippingInfo.address).length === 0) {
			fieldsToUpdate = _.omit(fieldsToUpdate, 'shippingInfo.address');
		}
		if (!fieldsToUpdate.shippingInfo.city) {
			fieldsToUpdate = _.omit(fieldsToUpdate, 'shippingInfo.city');
		}
		if (!fieldsToUpdate.shippingInfo.country) {
			fieldsToUpdate = _.omit(fieldsToUpdate, 'shippingInfo.country');
		}
		if (!fieldsToUpdate.shippingInfo.state) {
			fieldsToUpdate = _.omit(fieldsToUpdate, 'shippingInfo.state');
		}
		if (!fieldsToUpdate.shippingInfo.postalCode) {
			fieldsToUpdate = _.omit(fieldsToUpdate, 'shippingInfo.postalCode');
		}

		this.user.shippingInfo = fieldsToUpdate.shippingInfo;

		this.us.saveProfile(this.user._id, fieldsToUpdate).subscribe();
		this.us.updateUser(this.user);
		this.us.updateAuthor(this.user);
		this.updateStripeUser();
	}

	async updateStripeUser() {
		const [error, resultValues] = await to(this.stripeAddress.getValue());
		if (error) {
			throw error;
		}

		const { value } = resultValues;
		const addressElement = value || null;

		if (!addressElement) return;

		const { address, name } = addressElement;
		const fieldsToUpdate = {
			name,
			address,
		};

		this.ss.updateCustomerInfo(this.user.stripeCustomerId, fieldsToUpdate).subscribe();
	}

	getStripePaymentMethods() {
		// If payments are already loaded, if there are no saved payments, or if the user has not opted into saved payments, return.

		if (this.paymentMethodsLoaded) {
			return;
		}

		if (!this.savedPaymentMethods.length) {
			return;
		}

		this.paymentMethodsLoaded = true;

		this.ss.getCustomerPaymentMethods(this.user.stripeCustomerId).subscribe((result: StripePaymentMethodsResponse) => {
			if (!result.success || !result.paymentMethods) {
				this.stripePaymentError = 'Unable to resolve payment methods';
				return;
			}

			const savedPaymentMethodIds = this.savedPaymentMethods.map((paymentMethod) => paymentMethod.paymentMethodId);
			const paymentMethods = result.paymentMethods.filter((item) => savedPaymentMethodIds.includes(item.id));

			const [primary] = paymentMethods;
			this.stripePrimaryPaymentMethod = primary;

			if (primary) {
				this.stripePaymentMethods = paymentMethods.filter(
					(paymentMethod: IPopulatedStripePaymentMethod) => paymentMethod.id !== primary.id,
				);
			} else {
				this.stripePaymentMethods = paymentMethods;
			}
		});
	}

	selectedStripeCard(paymentMethodId) {
		this.stripeSelectedPaymentMethodId = paymentMethodId;
	}

	setValue(event, target) {
		if (!this.form.controls[target]) return;

		this.form.controls[target].patchValue(event.target.value);
	}

	_generateStripeForm() {
		const appearance: Appearance = {
			theme: 'flat',
			variables: { colorPrimaryText: '#262626' },
		};

		const cardOptions = {
			style: {
				base: {
					fontSize: '16px',
				},
			},
			hidePostalCode: true,
		};
		this.elements = this.stripe.elements({ appearance });
		this.stripeCardElement = this.elements.create('card', cardOptions);
		this.stripeCardElement.mount('#stripe-card');
		this.stripeAddress = this.elements.create('address', this.addressOptions);
		this.stripeAddress.mount('#stripe-address');
	}

	extractPayPalError(err) {
		if (err) {
			// Default the error message to what we know is included in the response
			let errorMessage = err.name ? `${err.name}: ` : '';
			errorMessage += err.message ? `${err.message}` : '';
			// Base case where we return the found string or number (error code)
			if (typeof err === 'string' || typeof err === 'number') {
				errorMessage = `\n${err}`; // concat onto new line
			} else if (typeof err === 'object') {
				// For each key found in the object, recursively loop through to find string or number
				Object.keys(err).forEach((key) => {
					errorMessage += `${this.extractPayPalError(err[key])} `;
				});
			}
			return errorMessage;
		}
		return '';
	}
}
